import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import roundTo from 'round-to';
import {
  timeParser,
  dateParser,
  dateTimeParser,
  inputFormats,
} from '../../util/DateTimeParsers';
import { Moment } from 'moment';

type InputValueTypes =
  | 'boolean'
  | 'decimal'
  | 'integer'
  | 'string'
  | 'text'
  | 'coding'
  | 'quantity'
  | 'time'
  | 'date'
  | 'dateTime';

export type DisplayValue = {
  display: string;
  value: string;
};

/**
 * Wrapper class for selection options to easily enable conversion and comparison
 */
export class SelectOption {
  _displayValue: DisplayValue;
  _formValue: string | number | fhir.Coding;

  constructor(
    formValue: string | number | fhir.Coding,
    inputValue: string | DisplayValue,
  ) {
    this._formValue = formValue;
    this._displayValue = isString(inputValue)
      ? { display: inputValue, value: inputValue }
      : inputValue;
  }

  get formValue() {
    return this._formValue;
  }

  get displayValue() {
    return this._displayValue;
  }

  /**
   * Determines if the selected dropdown value is equal to this select option
   */
  isValueOfOption(selectedValue: string) {
    return selectedValue === this.displayValue.value;
  }
}

/**
 * Form inputs are represented as string values, but we want the survey logic to use
 * defined fhir types (integers, decimals, quantity, code).  This performs the two way mapping
 */
interface ValueMapper<TFormType, TInputType> {
  inputValueType: InputValueTypes;
  toInputValue: (formValue: TFormType) => TInputType;
  fromInputValue: (
    inputValue: TInputType,
    originalFormValue: TFormType,
  ) => TFormType;
}

export const integerValueMapper: ValueMapper<number, string> = {
  inputValueType: 'integer',
  toInputValue: (formValue: number): string =>
    isNil(formValue) ? '' : String(formValue),
  fromInputValue: (inputValue: string, _originalFormValue: number): number => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (isNil(inputValue) || !isString(inputValue)) return null;

    const trimmed = inputValue.trim();

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (trimmed === '') return null;

    const parsedNumber = Number(trimmed);

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (isNaN(parsedNumber)) return null;

    // this could be a decimal value, floor it
    return Math.floor(parsedNumber);
  },
};

export const decimalValueMapper: ValueMapper<number, string> = {
  inputValueType: 'decimal',
  toInputValue: (formValue: number): string =>
    isNil(formValue) ? '' : String(formValue),
  fromInputValue: (inputValue: string, _originalFormValue: number): number => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (isNil(inputValue) || !isString(inputValue)) return null;

    const trimmed = inputValue.trim();

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (trimmed === '') return null;

    const parsedNumber = Number(trimmed);

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (isNaN(parsedNumber)) return null;

    return parsedNumber;
  },
};

export const booleanValueMapper: ValueMapper<boolean, string> = {
  inputValueType: 'boolean',
  toInputValue: (formValue: boolean): string => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (isNil(formValue)) return null;

    return formValue ? 'true' : 'false';
  },
  fromInputValue: (
    inputValue: string,
    _originalFormValue: boolean,
  ): boolean => {
    return inputValue === 'true';
  },
};

export const stringValueMapper: ValueMapper<string, string> = {
  inputValueType: 'string',
  toInputValue: (formValue: string): string =>
    isNil(formValue) || !isString(formValue) ? '' : formValue,
  fromInputValue: (inputValue: string, _originalFormValue: string): string =>
    inputValue,
};

export const textValueMapper: ValueMapper<string, string> = {
  ...stringValueMapper,
  inputValueType: 'text',
};

export const codingValueMapper: ValueMapper<fhir.Coding, DisplayValue> = {
  inputValueType: 'coding',
  toInputValue: (formValue: fhir.Coding): DisplayValue => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!formValue || !(formValue.code || formValue.display)) return null;

    return {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      display: formValue.display || formValue.code,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      value: formValue.code || formValue.display,
    };
  },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  fromInputValue: (
    inputValue: DisplayValue,
    originalFormValue: fhir.Coding,
  ) => {
    if (!originalFormValue) {
      // original is required or else other coding properties will be lost
      console.error(
        'Unnable to map a fhir coding from an input value without an original coding.',
      );
      return null;
    }

    if (!inputValue) return null;

    return {
      ...originalFormValue,
      code: inputValue.value,
      display: inputValue.display,
    };
  },
};

export const quantityValueMapper: ValueMapper<fhir.Quantity, string> = {
  inputValueType: 'quantity',
  toInputValue: (formValue: fhir.Quantity): string => {
    if (!formValue || isNil(formValue.value)) return '';

    return decimalValueMapper.toInputValue(formValue.value);
  },
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  fromInputValue: (inputValue: string, originalFormValue: fhir.Quantity) => {
    if (!originalFormValue) {
      // original is required or else other quantity properties will be lost
      console.error(
        'Unnable to map a fhir quantity from an input value without an original quantity.',
      );
      return null;
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const convertedValue = decimalValueMapper.fromInputValue(inputValue, null);
    if (convertedValue === null) return null;

    return {
      ...originalFormValue,
      value: convertedValue,
    };
  },
};

export const timeValueMapper: ValueMapper<fhir.time, Moment> = {
  inputValueType: 'time',
  toInputValue: (formValue: fhir.time): Moment => {
    return timeParser.toInputValue(formValue);
  },
  fromInputValue: (inputValue: Moment, _originalFormValue: fhir.time) => {
    return timeParser.fromInputValue(inputValue);
  },
};

export const dateValueMapper: ValueMapper<fhir.date, Moment> = {
  inputValueType: 'date',
  toInputValue: (formValue: fhir.date): Moment => {
    return dateParser.toInputValue(formValue);
  },
  fromInputValue: (inputValue: Moment, _originalFormValue: fhir.date) => {
    return dateParser.fromInputValue(inputValue);
  },
};

export const dateTimeValueMapper: ValueMapper<fhir.dateTime, Moment> = {
  inputValueType: 'dateTime',
  toInputValue: (formValue: fhir.dateTime): Moment => {
    return dateTimeParser.toInputValue(formValue);
  },
  fromInputValue: (inputValue: Moment, _originalFormValue: fhir.dateTime) => {
    return dateTimeParser.fromInputValue(inputValue);
  },
};

/**
 * transform an input value mapper to an array value mapper.  Note that this auto-filters null values
 */
export function toArrayValueMapper<TFormValue, TInputValue>(
  singleValueMapper: ValueMapper<TFormValue, TInputValue>,
): ValueMapper<TFormValue[], TInputValue[]> {
  return {
    inputValueType: singleValueMapper.inputValueType,
    toInputValue: (formValue: TFormValue[]): TInputValue[] => {
      if (!isArray(formValue)) return [];

      return formValue
        .map(singleFormValue => singleValueMapper.toInputValue(singleFormValue))
        .filter(Boolean);
    },
    fromInputValue: (
      inputValue: TInputValue[],
      originalFormValue: TFormValue[],
    ): TFormValue[] => {
      if (!isArray(inputValue)) return [];

      /*
        this implementation requires that all quantities and codes are of the same unit/system.  It makes
        sense to enforce this, but it may need to change in the future
      */
      const firstOriginalArrayValue =
        (originalFormValue && originalFormValue[0]) || null;

      return inputValue
        .map(singleInputValue =>
          singleValueMapper.fromInputValue(
            singleInputValue,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            firstOriginalArrayValue,
          ),
        )
        .filter(Boolean);
    },
  };
}

/**
 * Map fhir types to and from input form values
 */
export default class ValueMappers {
  static booleanTextField = booleanValueMapper;
  static integerTextField = integerValueMapper;
  static decimalTextField = decimalValueMapper;
  static stringTextField = stringValueMapper;
  static textTextField = textValueMapper;
  static quantityTextField = quantityValueMapper;
  static codingTextField = codingValueMapper;
  static timeField = timeValueMapper;
  static dateField = dateValueMapper;
  static dateTimeField = dateTimeValueMapper;
  static safeFormatFhirDate = (fhirValue: fhir.date) => {
    const inputValue = ValueMappers.dateField.toInputValue(fhirValue);
    return inputValue ? inputValue.format(inputFormats.date) : fhirValue;
  };
  static safeFormatFhirTime = (fhirValue: fhir.time) => {
    const inputValue = ValueMappers.timeField.toInputValue(fhirValue);
    return inputValue ? inputValue.format(inputFormats.time) : fhirValue;
  };

  static decimalTextFieldEnforcedPrecision = (precision: number) => ({
    ...decimalValueMapper,
    fromInputValue: (inputValue: string, originalFormValue: number): number => {
      const stdValue = decimalValueMapper.fromInputValue(
        inputValue,
        originalFormValue,
      );
      return isNil(stdValue) || isNil(precision)
        ? stdValue
        : roundTo(stdValue, precision);
    },
  });
  static quantityTextFieldEnforcedPrecision = (precision: number) => ({
    ...quantityValueMapper,
    fromInputValue: (
      inputValue: string,
      originalFormValue: fhir.Quantity,
    ): fhir.Quantity => {
      const stdValue = quantityValueMapper.fromInputValue(
        inputValue,
        originalFormValue,
      );
      return isNil(stdValue) || isNil(stdValue.value) || isNil(precision)
        ? stdValue
        : {
            ...stdValue,
            value: roundTo(stdValue.value, precision),
          };
    },
  });
  static integerToSelectOption = (formValue: number) => {
    const inputValue = integerValueMapper.toInputValue(formValue);
    if (inputValue === null) return null;

    return new SelectOption(formValue, inputValue);
  };

  static stringToSelectOption = (formValue: string) => {
    const inputValue = stringValueMapper.toInputValue(formValue);
    if (inputValue === null) return null;

    return new SelectOption(formValue, inputValue);
  };

  static codingToSelectOption = (formValue: fhir.Coding) => {
    const inputValue = codingValueMapper.toInputValue(formValue);

    if (inputValue === null) return null;

    return new SelectOption(formValue, inputValue);
  };

  static dateToSelectOption = (formValue: fhir.date) => {
    const inputValue = dateValueMapper.toInputValue(formValue);

    if (inputValue === null) return null;

    const displayValue: DisplayValue = {
      value: formValue,
      display: inputValue.format(inputFormats.date),
    };
    return new SelectOption(formValue, displayValue);
  };

  static timeToSelectOption = (formValue: fhir.time) => {
    const inputValue = timeValueMapper.toInputValue(formValue);

    if (inputValue === null) return null;

    const displayValue: DisplayValue = {
      value: formValue,
      display: inputValue.format(inputFormats.time),
    };
    return new SelectOption(formValue, displayValue);
  };

  static integersToSelectOptions = (formValues: number[]) => {
    return (formValues || [])
      .map(formValue => ValueMappers.integerToSelectOption(formValue))
      .filter(selectOption => selectOption !== null);
  };

  static stringsToSelectOptions = (formValues: string[]) => {
    return (formValues || [])
      .map(formValue => ValueMappers.stringToSelectOption(formValue))
      .filter(selectOption => selectOption !== null);
  };

  static codingsToSelectOptions = (formValues: fhir.Coding[]) => {
    return (formValues || [])
      .map(formValue => ValueMappers.codingToSelectOption(formValue))
      .filter(selectOption => selectOption !== null);
  };

  static dateToSelectOptions = (formValues: fhir.date[]) => {
    return (formValues || [])
      .map(formValue => ValueMappers.dateToSelectOption(formValue))
      .filter(selectOption => selectOption !== null);
  };

  static timeToSelectOptions = (formValues: fhir.time[]) => {
    return (formValues || [])
      .map(formValue => ValueMappers.timeToSelectOption(formValue))
      .filter(selectOption => selectOption !== null);
  };
}
